import React, { memo, useRef, type RefObject } from 'react';
import {
  makeStyles, mergeClasses, shorthands, tokens,
} from '@fluentui/react-components';
import { useAdsCopilotPilotMap } from './hooks/use-ads-copilot-pilot-map';
import { useAutoToBottomScroller } from './hooks/useAutoToBottomScroller';
import { useAutoToTopScroll } from './hooks/useAutoToTopScroller';
import { AutoScrollerContentClassName } from './utils';

const useConverstationContainerStyles = makeStyles({
  container: {
    ...shorthands.flex(1),
    marginBottom: tokens.spacingVerticalL,
    overflowX: 'hidden',
    overflowY: 'auto',
    scrollBehavior: 'auto',
    position: 'relative',
    '&.hide-scroll': {
      '-ms-overflow-style': 'none',
      'scrollbar-width': 'none',
      '::-webkit-scrollbar': {
        display: 'none',
      },
    },
  },
  contentContainer: {
    maxWidth: '652px',
    justifySelf: 'center',
    width: '100%',
  },
  contentContainerRefreshUI: {
    minHeight: 'calc(100% + 20px)',
  },
});

interface IAutoScrollerProps {
  children: React.ReactNode;
  autoScrollContainerRef: RefObject<HTMLDivElement>;
  className?: string;
  style?: React.CSSProperties;
  isStartPointOfNewConversation?: boolean;
  shouldHideScroll?: boolean;
  isCustomStream?: boolean;
}

/**
 * `AutoScroller` helps to scroll to the bottom of the wrapped child container when addition of new child element
 * is detected inside the container refed by autoScrollContainerRef.
 *
 * **Why autoScrollContainerRef is needed?**
 * There might be a case where the container needs to observed for any new child element addition is different or
 * a part of a larger container that needs to be scrolled to the bottom.
 * For such cases, autoScrollContainerRef is needed.
 *
 * **Example:**
 * In the below code, the component to be observed is the `content` div which is refed by `autoScrollContainerRef`,
 * Whereas the `parent` div is the container that needs to be scrolled to the bottom as a whole.
 *
 * ```tsx
 * const autoScrollContainerRef = useRef<HTMLDivElement>(null);
 *
 * <AutoScroller autoScrollContainerRef={autoScrollContainerRef}>
 *  <div id="parent">
 *    <div id="header">
 *      // contains some predefined cards to be shown
 *    </div>
 *    <div id="content" ref={autoScrollContainerRef}>
 *      // here content will be added dynamically and need to be observed
 *      // **Note:** This container should have bottom padding/margin <= 8px as the area considered for
 *      // new element addition is 16px (threshold) from the bottom of the content container
 *    </div>
 *    <div id="footer">
 *     // contains some predefined footer content having loader, links, buttons, etc
 *    </div>
 *  </div>
 * </AutoScroller>
 * ```
 *
 * @param props - Props for the AutoScroller component
 * - `children` - ReactNode - The children to be rendered inside the AutoScroller
 * - `autoScrollContainerRef` - RefObject<HTMLDivElement> - The ref of the container to be
 * observed for new child element addition
 * - `className` - string - (optional) The class name to be applied to the `AutoScroller` container
 * - `style` - React.CSSProperties - (optional) The style object to be applied to the `AutoScroller` container
 * @returns JSX.Element
 *
 */

export const AutoScroller = memo((props: IAutoScrollerProps) => {
  const {
    children,
    autoScrollContainerRef,
    className = '',
    style = {},
    isStartPointOfNewConversation = false,
    shouldHideScroll,
    isCustomStream,
  } = props;

  const styles = useConverstationContainerStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const pilotMap = useAdsCopilotPilotMap();
  const { isRefreshUIEnabled, isFixedQuestionEnabled } = pilotMap;

  useAutoToBottomScroller({
    isStartPointOfNewConversation,
    autoScrollContainerRef,
    containerRef,
    isScrollerEnabled: !isFixedQuestionEnabled,
  });

  useAutoToTopScroll({
    isStartPointOfNewConversation,
    autoScrollContainerRef,
    containerRef,
    isScrollerEnabled: isFixedQuestionEnabled,
    isCustomStream,
  });

  return (
    <div
      ref={containerRef}
      style={style}
      className={mergeClasses(
        styles.container,
        'auto-scroller',
        shouldHideScroll && 'hide-scroll',
        className
      )}
    >
      <div className={mergeClasses(styles.contentContainer, AutoScrollerContentClassName, className, (isRefreshUIEnabled && !shouldHideScroll) ? styles.contentContainerRefreshUI : '')}>
        {children}
      </div>
    </div>
  );
});
